import { useCallback, useEffect, useRef, useState } from 'react';
import { EDIT_LEASE_HEARTBEAT_MS, EDIT_LEASE_TTL_MS, type EditLeaseInfo } from '@bits2schema/shared';
import { api } from '../api/client';

export interface EditLeaseState {
  lease: EditLeaseInfo | null;
  /** `true`, pokud lease patří přihlášenému uživateli — jen pak je editace povolená. */
  isMine: boolean;
  /** E14-US11 — explicitní převzetí, dostupné kdykoliv, ne jen po vypršení TTL. */
  takeover: () => Promise<void>;
  /** Vteřin od poslední obnovy leasu držitelem — pro banner „poslední aktivita před {n} s". */
  secondsSinceActivity: number | null;
}

/**
 * Epic 14 (E14-US10/US11) — měkký zámek souběžné editace. `acquireLease` je
 * bezpečné volat opakovaně bez ohledu na to, jestli lease právě držíme:
 * když ji držíme, funguje jako heartbeat (obnoví TTL); když ji drží někdo
 * jiný, jen zjistí, jestli se mezitím uvolnila — jeden interval tak
 * obsluhuje heartbeat i polling zároveň (epic-14 kap. 8).
 */
export function useEditLease(projectId: string): EditLeaseState {
  const [lease, setLease] = useState<EditLeaseInfo | null>(null);
  const [isMine, setIsMine] = useState(false);
  const projectIdRef = useRef(projectId);
  projectIdRef.current = projectId;

  const acquire = useCallback(async () => {
    try {
      const result = await api.acquireLease(projectIdRef.current);
      setLease(result.lease);
      setIsMine(result.isMine);
    } catch {
      // Přechodná chyba sítě — další tik intervalu to zkusí znovu; neshazovat UI kvůli jednomu výpadku.
    }
  }, []);

  const takeover = useCallback(async () => {
    const result = await api.takeoverLease(projectIdRef.current);
    setLease(result.lease);
    setIsMine(result.isMine);
  }, []);

  useEffect(() => {
    void acquire();
    const interval = window.setInterval(() => void acquire(), EDIT_LEASE_HEARTBEAT_MS);

    const releaseBeacon = () => {
      fetch(`/api/projects/${projectId}/lease`, { method: 'DELETE', keepalive: true }).catch(() => undefined);
    };
    window.addEventListener('beforeunload', releaseBeacon);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener('beforeunload', releaseBeacon);
      releaseBeacon();
    };
  }, [projectId, acquire]);

  const secondsSinceActivity = lease
    ? Math.max(0, Math.round((Date.now() - (Date.parse(lease.expiresAt) - EDIT_LEASE_TTL_MS)) / 1000))
    : null;

  return { lease, isMine, takeover, secondsSinceActivity };
}
